'use client';

import Content from '../Content';
import { BlogHeader } from './BlogHeader';
import { BookingForm } from './BookingForm';
import { GradientHeading } from './GradientHeading';
import { TableOfContent } from './TableOfContentSidebar';
import { useEffect, useState } from 'react';
import { useBlogTheme } from '@/hooks/useBlogTheme';
import useLocationsStore from '@/store/useLocationsStore';
import { BlogFAQs } from './BlogFAQs';
import { Breadcrumbs } from './Breadcrumbs';
import dynamic from 'next/dynamic';

// Lazy load below-fold components for better performance
const RelatedBlogs = dynamic(
  () => import('./RelatedBlogs').then((mod) => ({ default: mod.RelatedBlogs })),
  { loading: () => <div className="h-96" /> }
);

const NudgeCard = dynamic(() =>
  import('@/components/ui/new/NudgeCard').then((mod) => ({
    default: mod.NudgeCard,
  }))
);

const AgencyModal = dynamic(() => import('./AgencyModal'));

export const SingleBlog = ({
  blogData,
  blogContent,
  sidebarData,
  headings, // Pre-processed headings from server
}) => {
  const [activeScrollId, setActiveScrollId] = useState(null);
  const [mounted, setMounted] = useState(false);
  const { isBlogLightTheme } = useBlogTheme();
  const { setHasVariants, setLocations, setTexts } = useLocationsStore();

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const scrollToSectionId = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -100;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Use Intersection Observer instead of scroll event for better performance
  useEffect(() => {
    if (!headings || headings.length === 0) return;

    const observerOptions = {
      rootMargin: '-150px 0px 0px 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      // Find the first intersecting entry (topmost visible section)
      const intersectingEntries = entries.filter(
        (entry) => entry.isIntersecting
      );
      if (intersectingEntries.length > 0) {
        // Sort by position to get the topmost one
        intersectingEntries.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
        );
        setActiveScrollId(intersectingEntries[0].target.id);
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all heading elements
    headings.forEach(({ scrollIdForHeading }) => {
      const element = document.getElementById(scrollIdForHeading);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  // change body background on theme change
  useEffect(() => {
    const body = document.body;

    // Ensure body class matches the current theme state
    if (isBlogLightTheme) {
      body.classList.add('light_blog_theme');
    } else {
      body.classList.remove('light_blog_theme');
    }

    return () => {
      body.classList.remove('light_blog_theme');
    };
  }, [isBlogLightTheme]);

  useEffect(() => {
    setHasVariants(blogData?.has_variants);
    setLocations(blogData?.location_blogs);
    setTexts(blogData?.map_block_heading, blogData?.map_block_subheading);
  }, [blogData, setHasVariants, setLocations, setTexts]);

  return (
    <main
      suppressHydrationWarning
      className={`pt-[132px] md:pt-[154px] ${
        mounted && isBlogLightTheme ? 'text-black' : 'text-white'
      }`}
    >
      {/* Top Heading */}
      <div className="px-[30px] w-full max-w-[1760px] mx-auto">
        <header className="md:mb-[120px] mb-[80px]">
          <GradientHeading
            textArr={blogData?.blog_top_heading}
            normalText="Words that inspire, ideas that spark, "
            italicText="stories that stay"
          />
        </header>

        {/* Main blog section: single col < lg, 2-col at lg, 3-col at xl */}
        <section className="w-full lg:grid lg:grid-cols-[1fr_300px] lg:gap-x-[60px] lg:items-start xl:grid-cols-[290px_1fr_290px]">
          {/* Left TOC — only visible at xl+ to avoid squeezing content at lg */}
          <aside className="max-xl:hidden top-5 xl:sticky xl:col-start-1 xl:row-start-1">
            <TableOfContent
              headings={headings}
              onHeadingClick={scrollToSectionId}
              activeHeadingId={activeScrollId}
            />
          </aside>

          <main className="w-full min-w-0 overflow-hidden">
            <Breadcrumbs blogTitle={blogData?.name} blogSlug={blogData?.slug} />
            <BlogHeader
              blogTitle={blogData?.name}
              author={{
                name: blogData?.author,
                avatar: blogData?.author_profile,
                bio: blogData?.author_short_bio,
              }}
              banner={blogData?.banner}
              publishedOn={blogData?.published_date}
              readTime={blogData?.content_read_time}
            />
            {/* Mobile TOC — shown below xl (TOC aside is hidden below xl) */}
            <div className="xl:hidden">
              <TableOfContent
                headings={headings}
                onHeadingClick={scrollToSectionId}
                activeHeadingId={activeScrollId}
              />
            </div>
            <Content content={blogContent} />
            {/* Mobile/tablet BookingForm — shown below lg where the right aside is hidden */}
            <div className="lg:hidden">
              <BookingForm data={sidebarData} isLightTheme={isBlogLightTheme} />
            </div>
          </main>

          <aside className="max-lg:hidden top-5 lg:sticky">
            <BookingForm data={sidebarData} isLightTheme={isBlogLightTheme} />
          </aside>
        </section>

        {/* FAQs Section */}
        {blogData?.faqs && blogData.faqs.length > 0 && (
          <div className="w-full md:pt-24 pt-16 pb-10 max-w-3xl mx-auto">
            <BlogFAQs
              faqs={blogData.faqs}
              heading={blogData?.faq_heading || 'Frequently Asked Questions'}
            />
          </div>
        )}
      </div>

      <RelatedBlogs
        heading={blogData.related_articles_heading}
        blogs={blogData.related_articles}
      />
      <NudgeCard page="blog" />
      {blogData?.modal_form && <AgencyModal data={blogData?.modal_form} />}
    </main>
  );
};
